import React, { useCallback, useEffect, useState } from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import { Toggle } from "@/components/Toggle";
import { SchedulingModel, WorkSchedule } from "./types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import ScheduleEditor from "./ScheduleEditor";
import { hasConflicts, scheduleSummary } from "./scheduleUtils";
import daysOfWeek from "./helpers/daysOfWeek";

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-12">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-slate-400">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
      <p className="text-slate-600 font-medium mb-1">Select a schedule to edit</p>
      <p className="text-slate-400 text-sm">Or create a new one with the button above.</p>
    </div>
  );
}

function ScheduleCard({
  schedule,
  selected,
  onClick,
  onDelete
}: {
  schedule: WorkSchedule;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  const agentCount = schedule.userIds?.length ?? 0;
  const teamCount  = schedule.teamIds?.length  ?? 0;
  const conflicts  = hasConflicts(schedule.shifts ?? []);

  return (
    <button
      className={`w-full text-left px-3 py-3 rounded-lg transition-colors border group ${
        selected
          ? "bg-primary-bg border-cornflower-blue-200 text-cornflower-blue-800"
          : "bg-white border-transparent hover:bg-slate-50 hover:border-slate-200 text-slate-700"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className="text-sm font-medium truncate">{schedule.name}</span>
            {!schedule.enabled && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium shrink-0">
                Disabled
              </span>
            )}
            {(schedule.priority ?? 100) < 100 && (
              <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium shrink-0">
                P{schedule.priority}
              </span>
            )}
            {schedule.validTo && new Date(schedule.validTo) > new Date() && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium shrink-0">
                Expires {schedule.validTo.slice(0, 10)}
              </span>
            )}
            {schedule.validTo && new Date(schedule.validTo) <= new Date() && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium shrink-0">
                Expired
              </span>
            )}
            {conflicts && (
              <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium shrink-0">
                Conflict
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 truncate">
            {scheduleSummary(schedule)}
          </div>
          <div className="flex gap-3 mt-1.5">
            {agentCount > 0 && (
              <span className="text-[11px] text-slate-500">
                {agentCount} agent{agentCount !== 1 ? "s" : ""}
              </span>
            )}
            {teamCount > 0 && (
              <span className="text-[11px] text-slate-500">
                {teamCount} team{teamCount !== 1 ? "s" : ""}
              </span>
            )}
            {agentCount === 0 && teamCount === 0 && (
              <span className="text-[11px] text-amber-500">No agents or teams</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 p-0.5"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete schedule"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </button>
  );
}

export default function ScheduleManager() {
  const [schedulingModel, setSchedulingModel] = useState<SchedulingModel>({
    enableScheduling: false,
    unscheduledTeamsAlwaysUnavailable: false,
    schedules: []
  });
  const [selectedId, setSelectedId] = useState<number | "new" | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const api = useApi();
  const { displayToast } = useToastAlert();

  const fetchSchedules = useCallback(async () => {
    try {
      const resp = await api.messaging.get("WorkSchedules/List");
      const model = await resp.json();
      setSchedulingModel({
        enableScheduling: model.enableScheduling ?? false,
        unscheduledTeamsAlwaysUnavailable: model.unscheduledTeamsAlwaysUnavailable ?? false,
        schedules: Array.isArray(model.schedules) ? model.schedules.map((s: WorkSchedule) => ({
          ...s,
          teamIds: s.teamIds ?? [],
          priority: s.priority ?? 100
        })) : []
      });
    } catch {
      // keep empty defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  function markDirty(model: SchedulingModel) {
    setSchedulingModel({ ...model });
    setPendingChanges(true);
  }

  async function onSave() {
    try {
      await api.messaging.post("WorkSchedules", schedulingModel);
      setPendingChanges(false);
      displayToast(Severity.Success, "Schedules saved");
      await fetchSchedules(); // reload real IDs from server (newly created schedules had temp negative IDs)
    } catch {
      displayToast(Severity.Error, "Failed to save schedules");
    }
  }

  function onAddSchedule() {
    const newSchedule: WorkSchedule = {
      id: -(Date.now()), // temp negative ID until backend assigns one
      name: "New Schedule",
      enabled: true,
      shifts: [{
        startDayOfWeek: daysOfWeek.Monday,
        startHour: 9, startMinute: 0,
        endDayOfWeek: daysOfWeek.Monday,
        endHour: 17, endMinute: 0
      }],
      userIds: [],
      teamIds: [],
      priority: 100
    };
    const updated = { ...schedulingModel, schedules: [...schedulingModel.schedules, newSchedule] };
    setSchedulingModel(updated);
    setPendingChanges(true);
    setSelectedId(newSchedule.id);
  }

  function onUpdateSchedule(updated: WorkSchedule) {
    const schedules = schedulingModel.schedules.map((s) =>
      s.id === updated.id ? updated : s
    );
    markDirty({ ...schedulingModel, schedules });
  }

  function onDeleteSchedule(id: number) {
    const schedules = schedulingModel.schedules.filter((s) => s.id !== id);
    markDirty({ ...schedulingModel, schedules });
    if (selectedId === id) setSelectedId(null);
    setConfirmDeleteId(null);
    displayToast(Severity.Success, "Schedule removed");
  }

  const selectedSchedule = schedulingModel.schedules.find(
    (s) => s.id === selectedId
  ) ?? null;

  if (loading) {
    return <div className="p-8 text-slate-500 text-sm">Loading schedules…</div>;
  }

  return (
    <div className="h-full flex overflow-hidden">
      {/* ── Left sidebar ── */}
      <div className="w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
        {/* Global settings */}
        <div className="p-4 border-b border-slate-100 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-slate-700">Enable Scheduling</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Agents are only available during their shift
              </p>
            </div>
            <Toggle
              toggled={schedulingModel.enableScheduling}
              changeToggle={() => markDirty({ ...schedulingModel, enableScheduling: !schedulingModel.enableScheduling })}
            />
          </div>
          {schedulingModel.enableScheduling && (
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold text-slate-700">Unscheduled teams offline</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Teams with no schedule are always unavailable
                </p>
              </div>
              <Toggle
                toggled={schedulingModel.unscheduledTeamsAlwaysUnavailable}
                changeToggle={() => markDirty({ ...schedulingModel, unscheduledTeamsAlwaysUnavailable: !schedulingModel.unscheduledTeamsAlwaysUnavailable })}
              />
            </div>
          )}
        </div>

        {/* Schedule list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {schedulingModel.schedules.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6">No schedules yet.</p>
          )}
          {schedulingModel.schedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              selected={selectedId === schedule.id}
              onClick={() => setSelectedId(schedule.id)}
              onDelete={() => setConfirmDeleteId(schedule.id)}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="p-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onAddSchedule}
            className="flex items-center gap-1.5 text-sm text-primary hover:text-cornflower-blue-800 font-medium transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            New schedule
          </button>
          {pendingChanges && (
            <PrimaryBlueButton onClick={onSave} label="Save all" />
          )}
        </div>
      </div>

      {/* ── Right panel: editor ── */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {selectedSchedule ? (
          <ScheduleEditor
            schedule={selectedSchedule}
            allSchedules={schedulingModel.schedules}
            onChange={onUpdateSchedule}
          />
        ) : (
          <EmptyState />
        )}
      </div>

      {/* ── Delete confirmation ── */}
      {confirmDeleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-slate-800 mb-2">Remove schedule?</h3>
            <p className="text-sm text-slate-500 mb-5">
              This will unlink all agents and teams from this schedule. Rules using its
              conditions won&apos;t change. This can&apos;t be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                onClick={() => onDeleteSchedule(confirmDeleteId)}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
